import { useEffect, useState } from "react";
import { orders as api, type Order } from "../../../api";
import { Link, useRouter } from "../../../router";
import { formatCurrency } from "../../portfolio/format";
import {
  instrumentLabel,
  quantityLabel,
  sideLabel,
  statusLabel,
  formatDate,
} from "../../orders/format";

// All orders across this investor's accounts, newest first. Rows link to the
// full order detail in the Orders section.
export function InvestorOrders({ investorId }: { investorId: number }) {
  const { navigate } = useRouter();
  const [items, setItems] = useState<Order[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .list({ investor_id: investorId })
      .then((d) => !cancelled && setItems(d.orders))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [investorId]);

  if (error) return <div className="banner-error">{error}</div>;
  if (items === null) return <div className="muted">Loading…</div>;

  if (items.length === 0) {
    return (
      <div className="empty">
        <p>No orders for this investor yet.</p>
        <Link to="/app/orders/new" className="btn btn-ghost">
          New order
        </Link>
      </div>
    );
  }

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Created</th>
          <th>Account</th>
          <th>Side</th>
          <th>Instrument</th>
          <th className="num">Quantity</th>
          <th>Status</th>
          <th className="num">Value</th>
        </tr>
      </thead>
      <tbody>
        {items.map((o) => (
          <tr
            key={o.id}
            className="row-link"
            onClick={() => navigate(`/app/orders/${o.id}`)}
          >
            <td>{formatDate(o.created_at)}</td>
            <td>{o.account_name}</td>
            <td>
              <span className={`pill side-${o.side}`}>{sideLabel(o.side)}</span>
            </td>
            <td>{instrumentLabel(o)}</td>
            <td className="num">
              {quantityLabel(o.quantity_mode, o.quantity, o.account_currency)}
            </td>
            <td>
              <span className={`pill status-${o.status}`}>
                {statusLabel(o.status)}
              </span>
            </td>
            <td className="num">
              {o.executed_value != null
                ? formatCurrency(o.executed_value, o.account_currency)
                : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
